<template>
  <v-container fluid grid-list-xl>
    <v-card>
      <v-toolbar
        color="secondary"
        dark
      >
        <v-toolbar-side-icon><v-icon>group</v-icon></v-toolbar-side-icon>
        <v-toolbar-title>{{$tc('admin.specialWidget', 2)}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <div
          v-if="$vuetify.breakpoint.smAndDown"
          slot="extension"
          class="white--text w-100"
        >
          <v-text-field
            type="text"
            v-model="filterText"
            @keyup.enter="doFilter"
            :placeholder="$t('admin.specialWidgetsFilterPlaceholder')"
            single-line
            hide-details
          ></v-text-field>
        </div>
        <v-text-field
          v-if="$vuetify.breakpoint.mdAndUp"
          type="text"
          v-model="filterText"
          @keyup.enter="doFilter"
          :placeholder="$t('admin.specialWidgetsFilterPlaceholder')"
          single-line
          hide-details
        ></v-text-field>
        <v-btn
          @click.prevent="doFilter"
          icon
        >
          <v-icon>search</v-icon>
        </v-btn>

        <v-btn
          @click.prevent="resetFilter"
          icon
        >
          <v-icon>restore</v-icon>
        </v-btn>
        <transition
          enter-active-class="animated bounceIn"
          leave-active-class="animated bounceOut"
        >
          <v-btn
            v-if="selectedRows !== undefined && selectedRows.length !== 0"
            @click.prevent="massDeleteModalVisible = true"
            icon
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </transition>
      </v-toolbar>
      <v-card-text>
        <v-progress-linear
          v-show="loading"
          slot="progress"
          color="accent"
          indeterminate
        ></v-progress-linear>
        <vuetable ref="vuetable"
          :api-url="`${this.apiUrl}api/widgets/special/superadmin`"
          data-path="data"
          pagination-path="meta"
          track-by="id"
          :http-fetch="httpFetch"
          :fields="fields"
          :css="css.table"
          :sort-order="sortOrder"
          :multi-sort="true"
          :append-params="moreParams"
          :auto-process-queue="false"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:loading="onLoading"
          @vuetable:loaded="onLoaded"
        >
          <div
            slot="special-widget-preview-slot"
            slot-scope="props"
            class="py-2"
          >
            <img
              :src="props.rowData.preview"
              height="100"
              class="img-preview"
            />
          </div>
          <div slot="actions-slot" slot-scope="props">
            <v-btn
              @click="showEditSpecialWidgetDialog(props.rowData)"
              flat
              icon
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-btn
              @click="showDeleteSpecialWidgetDialog(props.rowData)"
              flat
              icon
            >
              <v-icon>delete</v-icon>
            </v-btn>
          </div>
        </vuetable>
      </v-card-text>

      <v-card-actions>
        <vuetable-pagination ref="pagination"
          :css="css.pagination"
          @vuetable-pagination:change-page="onChangePage"
        ></vuetable-pagination>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="createModalVisible" max-width="900px">
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $t("admin.createSpecialWidget") }}</h3>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12>
              <vue-transmit
                drag-class="dragging"
                tag="div"
                v-bind="options"
                upload-area-classes="bg-faded"
                ref="uploader"
                @accept-complete="saveSpecialWidgetFile"
                @success="onUploadSuccess"
                @success-multiple="onUploadSuccessMulti"
                @error="onUploadError"
                @error-multiple="onUploadErrorMulti"
              >
                <div
                  class="
                    v-btn v-btn--block v-btn--outline v-btn--depressed theme--light accent--text"
                  style="height: 15rem;"
                >
                  <v-layout row wrap>
                    <v-flex xs12 text-xs-center>
                      <v-icon class="accent--text" x-large>file_upload</v-icon>
                    </v-flex>
                    <v-flex xs12 text-xs-center>
                      <h1>{{$tc('admin.icon', 1)}}</h1>
                    </v-flex>
                  </v-layout>
                </div>
                <template slot="files" slot-scope="props">
                  <v-layout row wrap>
                    <v-flex
                      v-for="file in props.files"
                      :key="file.id"
                      xs6
                      md4
                      lg2
                    >
                      <v-card>
                        <v-img :src="file.dataUrl"></v-img>
                        <v-card-title primary-title>
                          <div>
                            <h3 class="headline">
                              <p style="word-break: break-all;">{{ file.name }}</p>
                            </h3>
                            <span>Type: {{ file.type }}</span>
                            <p>Size: {{ file.size }}</p>
                          </div>
                        </v-card-title>
                        <v-progress-linear
                          v-model="file.upload.progress"
                          color="accent"
                        ></v-progress-linear>
                      </v-card>
                    </v-flex>
                  </v-layout>
                </template>
              </vue-transmit>
            </v-flex>
            <v-flex xs12>
              <v-text-field
                v-model="newSpecialWidget.name"
                name="name"
                type="text"
                :label="$t('general.name')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="newSpecialWidget.organization_id"
                v-validate="{ required: true }"
                data-vv-name="organization"
                name="organization"
                item-text="name"
                item-value="id"
                :items="organizations"
                :label="$tc('admin.organization', 1)"
                :error="errors.has('organization')"
                :error-messages="errors.collect('organization')"
                single-line
                required
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="newSpecialWidget.type"
                v-validate="{ required: true }"
                name="type"
                :items="types"
                :label="$tc('general.type', 1)"
                :error="errors.has('type')"
                :error-messages="errors.collect('type')"
                single-line
                required
              ></v-select>
            </v-flex>
            <v-flex
              v-if="newSpecialWidget.type === 'audio'"
              xs12
            >
              <v-text-field
                v-model="newSpecialWidget.audio_source"
                name="audio_source"
                type="text"
                :label="$t('admin.audioSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-else-if="newSpecialWidget.type === 'image'"
              xs12
            >
              <v-text-field
                v-model="newSpecialWidget.image_source"
                name="image_source"
                type="text"
                :label="$t('admin.imageSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-if="newSpecialWidget.type === 'video'"
              xs12
            >
              <v-text-field
                v-model="newSpecialWidget.video_source"
                name="video_source"
                type="text"
                :label="$t('admin.videoSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-if="newSpecialWidget.type === 'pdf'"
              xs12
            >
              <v-text-field
                v-model="newSpecialWidget.pdf_source"
                name="pdf_source"
                type="text"
                :label="$t('admin.pdfSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-if="newSpecialWidget.type === 'web'"
              xs12
            >
              <v-text-field
                v-model="newSpecialWidget.web_source"
                name="web_source"
                type="text"
                :label="$t('admin.webSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="createModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="createSpecialWidget()"
            color="primary"
          >{{ $t("general.create") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editModalVisible" max-width="900px">
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $t("admin.editSpecialWidget") }}</h3>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12>
              <v-text-field
                v-model="currentSpecialWidget.name"
                name="name"
                type="text"
                :label="$t('general.name')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="currentSpecialWidget.organization_id"
                v-validate="{ required: true }"
                data-vv-name="organization"
                name="organization"
                item-text="name"
                item-value="id"
                :items="organizations"
                :label="$tc('admin.organization', 1)"
                :error="errors.has('organization')"
                :error-messages="errors.collect('organization')"
                single-line
                required
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="currentSpecialWidget.type"
                v-validate="{ required: true }"
                name="type"
                :items="types"
                :label="$tc('general.type', 1)"
                :error="errors.has('type')"
                :error-messages="errors.collect('type')"
                single-line
                required
              ></v-select>
            </v-flex>
            <v-flex
              v-if="currentSpecialWidget.type === 'audio'"
              xs12
            >
              <v-text-field
                v-model="currentSpecialWidget.audio_source"
                name="audio_source"
                type="text"
                :label="$t('admin.audioSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-else-if="currentSpecialWidget.type === 'image'"
              xs12
            >
              <v-text-field
                v-model="currentSpecialWidget.image_source"
                name="image_source"
                type="text"
                :label="$t('admin.imageSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-if="currentSpecialWidget.type === 'video'"
              xs12
            >
              <v-text-field
                v-model="currentSpecialWidget.video_source"
                name="video_source"
                type="text"
                :label="$t('admin.videoSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-if="currentSpecialWidget.type === 'pdf'"
              xs12
            >
              <v-text-field
                v-model="currentSpecialWidget.pdf_source"
                name="pdf_source"
                type="text"
                :label="$t('admin.pdfSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
            <v-flex
              v-if="currentSpecialWidget.type === 'web'"
              xs12
            >
              <v-text-field
                v-model="currentSpecialWidget.web_source"
                name="web_source"
                type="text"
                :label="$t('admin.webSource')"
                single-line
                required
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="editModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="editSpecialWidget()"
            color="primary"
          >{{ $t("general.create") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteModalVisible" max-width="500px">
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("admin.deleteSpecialWidget", 1) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="deleteModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="deleteSpecialWidget()"
            flat
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="massDeleteModalVisible" max-width="500px">
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("admin.deleteSpecialWidget", 2) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="massDeleteModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="massDeleteSpecialWidgets()"
            flat
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-speed-dial
      fixed
      bottom
      right
      open-on-hover
    >
      <v-btn
        slot="activator"
        color="accent"
        dark
        fab
        hover
        @click="createModalVisible = true"
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-speed-dial>
  </v-container>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import VeeValidate from 'vee-validate';
import 'animate.css/animate.css';
import { VueTransmit } from 'vue-transmit';
import VueTableMaterialDesignCheckbox from '@/components/generic/VueTableMaterialDesignCheckbox.vue';
import VueTableMixin from '@/components/generic/VueTableMixin';

Vue.use(VeeValidate, { fieldsBagName: 'veeErrors' });

export default {
  components: {
    'vue-transmit': VueTransmit,
  },
  mixins: [
    VueTableMixin,
  ],
  data() {
    return {
      options: {
        autoProcessQueue: false,
        acceptedFileTypes: [
          'image/*',
        ],
        maxFileSize: 2000,
        clickable: true,
        uploadMultiple: false,
        maxConcurrentUploads: 1,
        adapterOptions: {
          url: '',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access-token')}`,
          },
        },
      },
      fields: [
        {
          name: VueTableMaterialDesignCheckbox,
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'id',
          title: 'ID',
          sortField: 'id',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'name',
          title: this.$i18n.t('general.name'),
          sortField: 'name',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'type',
          title: this.$i18n.t('general.type'),
          sortField: 'type',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'special-widget-preview-slot',
          title: this.$i18n.t('library.preview'),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
        },
        {
          name: 'actions-slot',
          title: this.$i18n.tc('general.action', 2),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
        },
      ],
      css: {
        table: {
          tableClass: 'v-table v-datatable',
          ascendingIcon: 'mdi mdi-sort-ascending',
          descendingIcon: 'mdi mdi-sort-descending',
        },
      },
      sortOrder: [
        { field: 'id', sortField: 'id', direction: 'asc' },
      ],
      currentSpecialWidget: {
        id: null,
        name: '',
        type: '',
        audio_source: '',
        image_source: '',
        pdf_source: '',
        video_source: '',
        web_source: '',
        organization_id: null,
      },
      newSpecialWidget: {
        file: null,
        name: '',
        type: '',
        audio_source: '',
        image_source: '',
        pdf_source: '',
        video_source: '',
        web_source: '',
        organization_id: null,
      },
      organizations: [],
      types: [
        { text: this.$i18n.t('design.audio'), value: 'audio' },
        { text: this.$i18n.t('design.image'), value: 'image' },
        { text: this.$i18n.t('design.video'), value: 'video' },
        { text: this.$i18n.t('design.pdf'), value: 'pdf' },
        { text: this.$i18n.t('design.web'), value: 'web' },
      ],
    };
  },
  mounted() {
    this.getItems(
      `${this.apiUrl}api/organizations`,
      {
        params: { paginate: false },
      },
      'organizations',
    );
  },
  methods: {
    triggerBrowse() {
      this.$refs.uploader.triggerBrowseFiles();
    },
    /* eslint-disable no-unused-vars */
    onUploadSuccess(file, res) {
      this.$refs.uploader.removeFile(file);
      this.$events.fire('refresh-special-widgets-table');
    },
    onUploadSuccessMulti(files, res) {
      this.$refs.uploader.removeAllFiles(false);
      this.$events.fire('refresh-special-wdigets-table');
    },
    onUploadError(file, msg, data) {
      this.$refs.uploader.removeFile(file);
      this.$toasted.show(msg, { duration: 3000, singleton: true, type: 'error' });
    },
    onUploadErrorMulti(files, msg, data) {
      this.$refs.uploader.removeAllFiles(false);
      this.$toasted.show(msg, { duration: 3000, singleton: true, type: 'error' });
    },
    /* eslint-enable */
    showEditSpecialWidgetDialog(specialWidget) {
      this.currentSpecialWidget = specialWidget;
      this.editModalVisible = true;
    },
    showDeleteSpecialWidgetDialog(specialWidget) {
      this.currentSpecialWidget = specialWidget;
      this.deleteModalVisible = true;
    },
    deleteSpecialWidget() {
      this.deleteModalVisible = false;
      this.deleteItem('widget/special', this.currentSpecialWidget.id);
    },
    massDeleteSpecialWidgets() {
      this.massDeleteModalVisible = false;
      this.massDeleteItems('widgets/special', {
        data: {
          ids: this.selectedRows,
        },
      });
    },
    editSpecialWidget() {
      this.editModalVisible = false;
      this.editItem('widget/special', this.currentSpecialWidget.id, {
        name: this.currentSpecialWidget.name,
        type: this.currentSpecialWidget.type,
        audio_source: this.currentSpecialWidget.audio_source,
        image_source: this.currentSpecialWidget.image_source,
        pdf_source: this.currentSpecialWidget.pdf_source,
        video_source: this.currentSpecialWidget.video_source,
        web_source: this.currentSpecialWidget.web_source,
        organization_id: this.currentSpecialWidget.organization_id,
      });
    },
    saveSpecialWidgetFile(file) {
      this.newSpecialWidget.file = file;
    },
    createSpecialWidget() {
      const vm = this;
      const itemData = {
        name: this.newSpecialWidget.name,
        type: this.newSpecialWidget.type,
        audio_source: this.newSpecialWidget.audio_source,
        image_source: this.newSpecialWidget.image_source,
        pdf_source: this.newSpecialWidget.pdf_source,
        video_source: this.newSpecialWidget.video_source,
        web_source: this.newSpecialWidget.web_source,
        organization_id: this.newSpecialWidget.organization_id,
      };

      axios.post(`${this.apiUrl}api/widget/special`, itemData)
        .then((response) => {
          vm.$toasted.show(response.data.message, { duration: 3000, singleton: true, type: 'success' });
          vm.createSpecialWidgetIcon(response.data.data.id);
          vm.$refs.vuetable.reload();
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });

      this.createModalVisible = false;
      this.resetNewSpecialWidgetData();
    },
    createSpecialWidgetIcon(specialWidgetID) {
      this.options.adapterOptions.url = `${window.api_url}api/widget/special/${specialWidgetID}/icon`;
      this.$refs.uploader.processFile(this.newSpecialWidget.file);
    },
    resetNewSpecialWidgetData() {
      this.newSpecialWidget.name = '';
      this.newSpecialWidget.type = '';
      this.newSpecialWidget.audio_source = '';
      this.newSpecialWidget.image_source = '';
      this.newSpecialWidget.pdf_source = '';
      this.newSpecialWidget.video_source = '';
      this.newSpecialWidget.web_source = '';
      this.newSpecialWidget.organization_id = null;
    },
  },
};
</script>

<style>
.v-transmit__upload-area:hover {
  cursor: pointer;
}

.v-transmit__upload-area--is-dragging {
  background: #fce4ec linear-gradient(
    -45deg,#fafafa 25%,
    transparent 0,
    transparent 50%,
    #fafafa 0,
    #fafafa 75%,
    transparent 0,
    transparent
  );
  background-size: 40px 40px;
}

.img-preview {
  vertical-align: middle;
}

</style>
